import useSWR from "swr";
import { fetcher } from "../../../../services/fetcher";
import { useOutletContext } from "react-router-dom";
import MainLoader from "../../../../common/components/Loader/Loader";
import { Stack, Text } from "@mantine/core";
import { useSelector } from "react-redux";
import DataWrapper from "../../../../common/components/DataWrapper/DataWrapper";
import dayjs from "dayjs";
import { filterOptions } from "../../../master-wallet/utils/transaction";

const walletEndpoint = "asc-wallet/user-wallet";
const addFundEndpoint = "asc-wallet/user-wallet/transactions/";

const OperatorWallet = () => {
  const outletContext = useOutletContext();
  const operatorProfile = useSelector((state) => state.operatorProfile);

  const operator = outletContext ? outletContext.operator : operatorProfile;

  const email = operator ? operator.operator.email : null;
  const transactionEndpoint = `${addFundEndpoint}?user_email=${email}`;

  const walletInfoEndpoint = `${walletEndpoint}?email=${email}`;

  const {
    isLoading: isWalletLoading,
  } = useSWR(walletInfoEndpoint, fetcher, {
    dedupingInterval: 60000,
  });

  const headerGradientStyle = {
    color: "transparent",
    backgroundImage: "linear-gradient(to left, #0053A8, #033F7C)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    display: "inline-block",
  };

  const config = {
    selectionCheckbox: true,
    selectionColumnWidth: 60,
    selectionCheckboxSize: 20,
    selectionCheckboxRadius: 4,
    selectionCheckboxStyles: {
      input: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderColor: "#AEB6BE",
      },
    },
    selectionHeaderStyle: {
      height: 54,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 24,
      paddingRight: 16,
    },
    selectionCellStyle: {
      height: 54,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 24,
      paddingRight: 16,
    },
    tableStyles: {
      th: {
        height: 54,
        padding: 0,
        borderBottom: "1px solid #D6E4EF",
        verticalAlign: "middle",
      },
      td: {
        height: 54,
        padding: 0,
        verticalAlign: "middle",
      },
    },
    horizontalSpacing: 0,
    verticalSpacing: 0,
    highlightOnHover: false,
    toolbarLeftGap: 12,
    searchInputWidth: 248,
    searchInputHeight: 48,
    searchIconSize: 16,
    searchInputStyles: {
      input: {
        height: 48,
        minHeight: 48,
        backgroundColor: "#FFFFFF",
        borderRadius: 8,
        border: "1px solid rgba(108, 148, 180, 0.15)",
        paddingLeft: 40,
        paddingRight: 16,
        color: "#6C94B4",
        fontSize: 14,
        fontWeight: 400,
        lineHeight: "normal",
      },
    },
    filterTriggerProps: {
      variant: "filled",
      color: "#FFFFFF",
      radius: 8,
      w: 48,
      h: 48,
      p: 0,
      style: {
        border: "1px solid rgba(108, 148, 180, 0.15)",
      },
    },
    filterIconProps: { size: 18 },
    fields: [
      {
        key: "transaction_number",
        label: "Transaction number",
        sortable: false,
        width: 164,
        headerControlStyle: { width: "100%", padding: 0 },
        labelStyle: {
          width: 164,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: "18px",
          ...headerGradientStyle,
        },
        headerStyle: { height: 54, padding: 0 },
        cellStyle: { height: 54, padding: 0 },
        render: (row) => (
          <Text c="#092C4C" fw={500} fz={14} lh="18px" w={164}>
            {row.transaction_number}
          </Text>
        ),
      },
      {
        key: "amount",
        label: "Amount",
        sortable: false,
        width: 96,
        headerControlStyle: { width: "100%", padding: 0 },
        labelStyle: {
          width: 96,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: "18px",
          ...headerGradientStyle,
        },
        headerStyle: { height: 54, padding: 0 },
        cellStyle: { height: 54, padding: 0 },
        render: (row) => (
          <Text c="#6C94B4" fw={500} fz={14} lh="18px" w={96}>
            {row.amount}
          </Text>
        ),
      },
      {
        key: "transaction_type",
        label: "Type",
        sortable: false,
        width: 70,
        headerControlStyle: { width: "100%", padding: 0 },
        labelStyle: {
          width: 70,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: "18px",
          ...headerGradientStyle,
        },
        headerStyle: { height: 54, padding: 0 },
        cellStyle: { height: 54, padding: 0 },
        render: (row) => (
          <Text c="#6C94B4" fw={500} fz={14} lh="18px" w={156}>
            {row.transaction_type}
          </Text>
        ),
      },
      {
        key: "created_by",
        label: "Created By",
        sortable: false,
        width: 156,
        headerControlStyle: { width: "100%", padding: 0 },
        labelStyle: {
          width: 156,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: "18px",
          ...headerGradientStyle,
        },
        headerStyle: { height: 54, padding: 0 },
        cellStyle: { height: 54, padding: 0 },
        render: (row) => (
          <Text c="#6C94B4" fw={500} fz={14} lh="18px" w={156}>
            {row.created_by}
          </Text>
        ),
      },
      {
        key: "created_at",
        label: "Date",
        sortable: false,
        width: 148,
        headerControlStyle: { width: "100%", padding: 0 },
        labelStyle: {
          width: 148,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: "18px",
          ...headerGradientStyle,
        },
        headerStyle: { height: 54, padding: 0 },
        cellStyle: { height: 54, padding: 0 },
        render: (row) => (
          <Text c="#6C94B4" fw={500} fz={14} lh="18px" w={148}>
            {row.created_at
              ? dayjs(row.created_at).format("DD-MM-YYYY hh:mm a")
              : ""}
          </Text>
        ),
      },
    ],
  };

  if (isWalletLoading) {
    return <MainLoader />;
  }

  return (
    <Stack gap={0}>
      <DataWrapper
        endpoint={transactionEndpoint}
        config={config}
        filterOptions={filterOptions}
      />
    </Stack>
  );
};

export default OperatorWallet;